import { useState, useEffect, useCallback } from "react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, Cell, PieChart, Pie } from "recharts";
import { TrendingUp, TrendingDown, Brain, Target, Activity, BarChart3, Zap, RefreshCw, Award, AlertTriangle, ChevronDown, ChevronUp, Flame } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const getAuthHeaders = () => {
  const token = localStorage.getItem("apex_token");
  return token ? { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } : { "Content-Type": "application/json" };
};

const COLORS = ["#10b981", "#06b6d4", "#8b5cf6", "#f59e0b", "#ef4444", "#ec4899", "#14b8a6", "#6366f1"];

export default function BotPerformance({ onAnalyze, positions: parentPositions, account: parentAccount }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedCycle, setExpandedCycle] = useState(null);
  const [topMovers, setTopMovers] = useState([]);

  const fetchPerformance = useCallback(async () => {
    setLoading(true);
    try {
      const [perfRes, moversRes] = await Promise.all([
        fetch(`${API}/api/bot/performance`, { headers: getAuthHeaders() }),
        fetch(`${API}/api/bot/top-movers`, { headers: getAuthHeaders() }),
      ]);
      if (perfRes.ok) setData(await perfRes.json());
      if (moversRes.ok) {
        const m = await moversRes.json();
        setTopMovers(m.movers || []);
      }
    } catch (e) { console.error("Bot perf:", e); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchPerformance(); }, [fetchPerformance]);

  if (!data) return null;

  const { summary, t212_account, confidence_distribution, top_tickers, timeline, best_positions, worst_positions, recent_cycles } = data;
  const totalPnl = (t212_account?.unrealized_pnl || 0) + (t212_account?.realized_pnl || 0);
  const confData = Object.entries(confidence_distribution || {}).map(([k, v]) => ({ name: k, value: v }));

  // Compute win rate from parent positions if API didn't return it
  const effectiveWinRate = summary.win_rate > 0 ? summary.win_rate : (() => {
    if (!parentPositions || parentPositions.length === 0) return null;
    const w = parentPositions.filter(p => (p.pnl || 0) > 0).length;
    const l = parentPositions.filter(p => (p.pnl || 0) < 0).length;
    return (w + l) > 0 ? Math.round((w / (w + l)) * 100 * 10) / 10 : null;
  })();
  const effectiveWinning = summary.winning_positions > 0 ? summary.winning_positions : (parentPositions || []).filter(p => (p.pnl || 0) > 0).length;
  const effectiveLosing = summary.losing_positions > 0 ? summary.losing_positions : (parentPositions || []).filter(p => (p.pnl || 0) < 0).length;
  
  // Use parent account for P&L if API didn't return T212 data  
  const effectivePnl = t212_account?.total_value ? totalPnl : (parentAccount?.pnl || null);
  const effectiveRealizedPnl = t212_account?.realized_pnl || parentAccount?.realized_pnl || null;
  
  // Effective positions for best/worst
  const effectiveBest = best_positions.length > 0 ? best_positions : 
    (parentPositions || []).filter(p => (p.pnl || 0) > 0).sort((a, b) => b.pnl - a.pnl).slice(0, 5);
  const effectiveWorst = worst_positions.length > 0 ? worst_positions :
    (parentPositions || []).filter(p => (p.pnl || 0) < 0).sort((a, b) => a.pnl - b.pnl).slice(0, 5);

  return (
    <div className="space-y-4" data-testid="bot-performance">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-cyan-400" />
          <h3 className="text-sm font-black text-white uppercase tracking-wider">Performance du Bot</h3>
        </div>
        <button onClick={fetchPerformance} disabled={loading} className="p-1.5 rounded-lg hover:bg-slate-700/50 text-slate-400 hover:text-cyan-400 transition-colors" data-testid="refresh-perf">
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="perf-kpis">
        <div className="glass-card rounded-xl p-3 border border-slate-700/30">
          <div className="text-[9px] text-slate-500 mb-1 uppercase">Win Rate</div>
          <div className={`text-xl font-black ${effectiveWinRate && effectiveWinRate >= 60 ? "text-emerald-400" : effectiveWinRate && effectiveWinRate >= 40 ? "text-amber-400" : effectiveWinRate ? "text-red-400" : "text-slate-500"}`}>
            {effectiveWinRate != null ? `${effectiveWinRate}%` : "N/A"}
          </div>
          <div className="text-[9px] text-slate-600">{effectiveWinning}W / {effectiveLosing}L</div>
        </div>
        <div className="glass-card rounded-xl p-3 border border-slate-700/30">
          <div className="text-[9px] text-slate-500 mb-1 uppercase">Trades Total</div>
          <div className="text-xl font-black text-cyan-400">{summary.total_trades}</div>
          <div className="text-[9px] text-slate-600">{summary.successful_buys}B / {summary.successful_sells}S / {summary.failed_trades} fail</div>
        </div>
        <div className="glass-card rounded-xl p-3 border border-slate-700/30">
          <div className="text-[9px] text-slate-500 mb-1 uppercase">Confiance Moy.</div>
          <div className={`text-xl font-black ${summary.avg_confidence >= 75 ? "text-emerald-400" : "text-amber-400"}`}>
            {summary.avg_confidence}%
          </div>
          <div className="text-[9px] text-slate-600">sur {summary.total_trades} décisions</div>
        </div>
        {effectivePnl != null ? (
          <div className="glass-card rounded-xl p-3 border border-slate-700/30">
            <div className="text-[9px] text-slate-500 mb-1 uppercase">P&L Total</div>
            <div className={`text-xl font-black ${effectivePnl >= 0 ? "text-emerald-400" : "text-red-400"}`}>
              {effectivePnl >= 0 ? "+" : ""}{effectivePnl.toLocaleString("fr-FR")}€
            </div>
            {effectiveRealizedPnl != null && (
              <div className="text-[9px] text-slate-600">
                Réalisé: {effectiveRealizedPnl.toLocaleString("fr-FR")}€
              </div>
            )}
          </div>
        ) : (
          <div className="glass-card rounded-xl p-3 border border-slate-700/30">
            <div className="text-[9px] text-slate-500 mb-1 uppercase">Exécution</div>
            <div className="text-xl font-black text-violet-400">{Math.round(((summary.successful_buys + summary.successful_sells) / Math.max(summary.total_trades, 1)) * 100)}%</div>
            <div className="text-[9px] text-slate-600">taux de succès ordres</div>
          </div>
        )}
      </div>

      {/* Top Daily Movers (Explosive stocks the bot targets) WITH TECHNICAL ANALYSIS */}
      {topMovers.length > 0 && (
        <div className="glass-card rounded-xl p-4 border border-amber-500/20" data-testid="top-movers">
          <h4 className="text-xs font-bold text-white mb-3 flex items-center gap-2">
            <Flame className="w-3.5 h-3.5 text-amber-400" /> Explosions du Jour — Analyse Technique
          </h4>
          <div className="space-y-2">
            {topMovers.slice(0, 8).map((m, i) => {
              const ta = m.technical;
              const signalColor = !ta ? "text-slate-500" :
                ta.signal === "DANGER" ? "text-red-400" :
                ta.signal === "CAUTION" ? "text-amber-400" :
                ta.signal === "GOOD_ENTRY" ? "text-emerald-400" :
                ta.signal === "ACCEPTABLE" ? "text-cyan-400" :
                "text-slate-400";
              const signalBg = !ta ? "bg-slate-800/30" :
                ta.signal === "DANGER" ? "bg-red-500/5 border-red-500/15" :
                ta.signal === "CAUTION" ? "bg-amber-500/5 border-amber-500/15" :
                ta.signal === "GOOD_ENTRY" ? "bg-emerald-500/5 border-emerald-500/15" :
                ta.signal === "ACCEPTABLE" ? "bg-cyan-500/5 border-cyan-500/15" :
                "bg-slate-800/30";
              return (
                <div key={i} className={`p-2.5 rounded-lg border transition-all cursor-pointer hover:border-cyan-500/20 ${signalBg}`} onClick={() => onAnalyze?.(m.ticker)} data-testid={`mover-${m.ticker}`}>
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs font-bold text-cyan-400">{m.ticker}</span>
                      <span className="text-sm font-black text-emerald-400">+{m.change_1d?.toFixed(1)}%</span>
                      <span className="text-[9px] text-slate-500">${m.price?.toFixed(2)}</span>
                      {m.vol_ratio > 0 && <span className="text-[9px] text-amber-400/60">vol x{m.vol_ratio}</span>}
                    </div>
                    {ta && <span className={`text-[9px] font-bold ${signalColor}`}>{ta.signal}</span>}
                  </div>
                  {ta && (
                    <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[9px]">
                      <span className="text-slate-400">RSI: <strong className={ta.rsi > 70 ? "text-red-400" : ta.rsi < 30 ? "text-emerald-400" : "text-white"}>{ta.rsi}</strong></span>
                      <span className="text-slate-400">MACD: <strong className={ta.macd === "BULLISH" ? "text-emerald-400" : "text-red-400"}>{ta.macd}</strong></span>
                      <span className="text-slate-400">Vol: <strong className={ta.volume_trend === "EXPLOSIVE" ? "text-amber-400" : "text-slate-300"}>{ta.volume_trend}</strong></span>
                      <span className="text-slate-400">Range: <strong className={ta.range_pct > 80 ? "text-red-400" : ta.range_pct < 30 ? "text-emerald-400" : "text-white"}>{ta.range_pct?.toFixed(0)}%</strong></span>
                      {ta.patterns?.filter(p => p !== "NONE").map((p, j) => (
                        <span key={j} className={`font-bold ${p.includes("BEARISH") || p.includes("SHOOTING") || p.includes("RED") ? "text-red-400" : p.includes("BULLISH") || p.includes("HAMMER") || p.includes("GREEN") ? "text-emerald-400" : "text-amber-400"}`}>{p}</span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Activity Timeline + Confidence Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Timeline Chart */}
        {timeline.length > 0 && (
          <div className="glass-card rounded-xl p-4 border border-slate-700/30" data-testid="timeline-chart">
            <h4 className="text-xs font-bold text-white mb-3 flex items-center gap-2">
              <Activity className="w-3.5 h-3.5 text-cyan-400" /> Activité par Jour
            </h4>
            <ResponsiveContainer width="100%" height={160}>
              <BarChart data={timeline} barGap={2}>
                <XAxis dataKey="date" tick={{ fontSize: 9, fill: "#64748b" }} tickFormatter={d => d.slice(5)} />
                <YAxis tick={{ fontSize: 9, fill: "#64748b" }} width={25} />
                <Tooltip contentStyle={{ background: "#1e293b", border: "1px solid #334155", borderRadius: 8, fontSize: 11 }} />
                <Bar dataKey="buys" name="Achats" fill="#10b981" radius={[2, 2, 0, 0]} />
                <Bar dataKey="sells" name="Ventes" fill="#ef4444" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Confidence Distribution */}
        <div className="glass-card rounded-xl p-4 border border-slate-700/30" data-testid="confidence-chart">
          <h4 className="text-xs font-bold text-white mb-3 flex items-center gap-2">
            <Brain className="w-3.5 h-3.5 text-violet-400" /> Distribution Confiance IA
          </h4>
          <ResponsiveContainer width="100%" height={160}>
            <BarChart data={confData}>
              <XAxis dataKey="name" tick={{ fontSize: 9, fill: "#64748b" }} />
              <YAxis tick={{ fontSize: 9, fill: "#64748b" }} width={25} />
              <Tooltip contentStyle={{ background: "#1e293b", border: "1px solid #334155", borderRadius: 8, fontSize: 11 }} />
              <Bar dataKey="value" name="Trades" radius={[4, 4, 0, 0]}>
                {confData.map((_, i) => (
                  <Cell key={i} fill={COLORS[i % COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Best & Worst Positions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {effectiveBest.length > 0 && (
          <div className="glass-card rounded-xl p-4 border border-emerald-500/20" data-testid="best-positions">
            <h4 className="text-xs font-bold text-white mb-3 flex items-center gap-2">
              <Award className="w-3.5 h-3.5 text-emerald-400" /> Meilleures Positions
            </h4>
            <div className="space-y-1.5">
              {effectiveBest.map((p, i) => (
                <div key={i} className="flex items-center justify-between p-2 rounded-lg bg-slate-800/30 border border-slate-700/20 hover:border-emerald-500/20 transition-all cursor-pointer" onClick={() => onAnalyze?.(p.ticker)} data-testid={`best-pos-${p.ticker}`}>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-bold text-emerald-400/60 w-4">#{i + 1}</span>
                    <span className="font-mono text-xs font-bold text-cyan-400">{p.ticker}</span>
                    <span className="text-[9px] text-slate-500">x{p.quantity}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[9px] text-slate-500 font-mono">{p.avg_price?.toFixed(2)} → {p.current_price?.toFixed(2)}</span>
                    <span className="text-xs font-bold text-emerald-400 flex items-center gap-0.5">
                      <TrendingUp className="w-3 h-3" />+{p.pnl?.toLocaleString("fr-FR")}€
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {effectiveWorst.length > 0 && (
          <div className="glass-card rounded-xl p-4 border border-red-500/20" data-testid="worst-positions">
            <h4 className="text-xs font-bold text-white mb-3 flex items-center gap-2">
              <AlertTriangle className="w-3.5 h-3.5 text-red-400" /> Positions en Perte
            </h4>
            <div className="space-y-1.5">
              {effectiveWorst.filter(p => p.pnl < 0).map((p, i) => (
                <div key={i} className="flex items-center justify-between p-2 rounded-lg bg-slate-800/30 border border-slate-700/20 hover:border-red-500/20 transition-all cursor-pointer" onClick={() => onAnalyze?.(p.ticker)} data-testid={`worst-pos-${p.ticker}`}>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs font-bold text-cyan-400">{p.ticker}</span>
                    <span className="text-[9px] text-slate-500">x{p.quantity}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[9px] text-slate-500 font-mono">{p.avg_price?.toFixed(2)} → {p.current_price?.toFixed(2)}</span>
                    <span className="text-xs font-bold text-red-400 flex items-center gap-0.5">
                      <TrendingDown className="w-3 h-3" />{p.pnl?.toLocaleString("fr-FR")}€
                    </span>
                  </div>
                </div>
              ))}
              {effectiveWorst.filter(p => p.pnl < 0).length === 0 && (
                <div className="text-[10px] text-slate-500 text-center py-3">Aucune position en perte</div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Top Traded Tickers */}
      {top_tickers.length > 0 && (
        <div className="glass-card rounded-xl p-4 border border-slate-700/30" data-testid="top-tickers">
          <h4 className="text-xs font-bold text-white mb-3 flex items-center gap-2">
            <Target className="w-3.5 h-3.5 text-cyan-400" /> Tickers les Plus Tradés
          </h4>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
            {top_tickers.map((t, i) => (
              <div key={i} className="p-2 rounded-lg bg-slate-800/30 border border-slate-700/20 hover:border-cyan-500/20 transition-all cursor-pointer" onClick={() => onAnalyze?.(t.ticker)} data-testid={`top-ticker-${t.ticker}`}>
                <div className="font-mono text-xs font-bold text-cyan-400 mb-1">{t.ticker}</div>
                <div className="flex items-center gap-2 text-[9px]">
                  <span className="text-emerald-400">{t.buys}B</span>
                  <span className="text-red-400">{t.sells}S</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* AI Reasoning - Recent Cycles */}
      {recent_cycles?.length > 0 && (
        <div className="glass-card rounded-xl p-4 border border-violet-500/20" data-testid="ai-reasoning">
          <h4 className="text-xs font-bold text-white mb-3 flex items-center gap-2">
            <Brain className="w-3.5 h-3.5 text-violet-400" /> Raisonnement IA (derniers cycles)
          </h4>
          <div className="space-y-2">
            {recent_cycles.map((cycle, i) => {
              const isExpanded = expandedCycle === i;
              const actions = cycle.ai_decision?.actions || [];
              return (
                <div key={i} className="rounded-lg bg-slate-800/30 border border-slate-700/20 overflow-hidden" data-testid={`cycle-${i}`}>
                  <div className="flex items-center justify-between p-3 cursor-pointer hover:bg-slate-700/20 transition-colors" onClick={() => setExpandedCycle(isExpanded ? null : i)}>
                    <div className="flex items-center gap-3">
                      <span className="text-[10px] font-bold text-violet-400">#{cycle.cycle_number}</span>
                      <span className={`text-[9px] px-1.5 py-0.5 rounded font-bold ${
                        cycle.portfolio_health === "bon" ? "bg-emerald-500/15 text-emerald-400" :
                        cycle.portfolio_health === "danger" ? "bg-red-500/15 text-red-400" :
                        "bg-amber-500/15 text-amber-400"
                      }`}>{cycle.portfolio_health || "—"}</span>
                      <span className="text-[9px] text-slate-400">{actions.length} actions</span>
                      <span className="text-[9px] text-slate-600">{cycle.timestamp?.slice(0, 16)?.replace("T", " ")}</span>
                    </div>
                    {isExpanded ? <ChevronUp className="w-3 h-3 text-slate-500" /> : <ChevronDown className="w-3 h-3 text-slate-500" />}
                  </div>
                  {isExpanded && (
                    <div className="px-3 pb-3 space-y-2">
                      {/* Market Analysis */}
                      {cycle.market_analysis && (
                        <div className="p-2 rounded bg-slate-900/50 border border-slate-700/10">
                          <div className="text-[9px] text-violet-400 font-bold mb-1 uppercase">Analyse Marché</div>
                          <p className="text-[11px] text-slate-300 leading-relaxed">{cycle.market_analysis}</p>
                        </div>
                      )}
                      {/* Actions */}
                      {actions.length > 0 && (
                        <div className="space-y-1">
                          {actions.map((a, j) => (
                            <div key={j} className="flex items-start gap-2 text-[10px] p-1.5 rounded bg-slate-900/30">
                              <span className={`font-bold px-1.5 py-0.5 rounded shrink-0 ${
                                a.type === "BUY" ? "bg-emerald-500/15 text-emerald-400" :
                                a.type === "SELL" ? "bg-red-500/15 text-red-400" :
                                "bg-slate-500/15 text-slate-400"
                              }`}>{a.type}</span>
                              <span className="font-mono text-cyan-400 shrink-0 cursor-pointer hover:text-cyan-300" onClick={(e) => { e.stopPropagation(); onAnalyze?.(a.ticker); }}>{a.ticker}</span>
                              <span className="text-slate-500 shrink-0">x{a.quantity}</span>
                              {a.mode && <span className="text-[8px] px-1 py-0.5 rounded bg-violet-500/15 text-violet-400 shrink-0">{a.mode?.replace(/_/g, " ")}</span>}
                              {a.grade && <span className={`text-[8px] px-1 py-0.5 rounded shrink-0 font-bold ${a.grade === "A+" ? "bg-emerald-500/15 text-emerald-400" : a.grade === "A" ? "bg-cyan-500/15 text-cyan-400" : "bg-slate-500/15 text-slate-500"}`}>{a.grade}</span>}
                              <span className="text-slate-300 flex-1 truncate">{a.reason}</span>
                              {a.reward_risk_ratio > 0 && <span className="text-[8px] text-amber-400 shrink-0">R:R {a.reward_risk_ratio?.toFixed(1)}</span>}
                              <span className="text-white font-bold shrink-0">{a.confidence}%</span>
                            </div>
                          ))}
                        </div>
                      )}
                      {/* Next Focus */}
                      {cycle.next_focus && (
                        <div className="flex items-start gap-1.5 text-[9px] text-slate-500 pt-1 border-t border-slate-700/20">
                          <Zap className="w-3 h-3 text-amber-400 shrink-0 mt-0.5" />
                          <span><strong className="text-amber-400">Prochain focus:</strong> {cycle.next_focus}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
